import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import NonMotorPolicyDistributionChart, { PolicyStats } from './NonMotorPolicyDistributionChart';
import { getBrokerDashboardNonMotorPolicyData, getBrokerDashboardProtectionTypes } from '../../../../../api/requests';
import { useQuery } from '@tanstack/react-query';
import { getMonthNameFromDate } from '../../../../../utils/miscFunctions';
import useUserAccount from '../../../../../hooks/userAccountHook';
import MotorPolicyDistributionChart from './MotorPolicyDistributionChart';
import DashboardDatePickers, { SelectedDateRange } from '../../../../../components/dashboard/date-range-selector/datePicker';


const PolicyDistribution: React.FC = () => {
    const [activeTab, setActiveTab] = useState<IPolicyTypes>('motor');
    const [screenWidth, setScreenWidth] = React.useState<number>(document.documentElement.clientWidth);
    const [motorSelectedDateRange, setMotorSelectedDateRange] = useState<SelectedDateRange>();
    const [nonMotorSelectedDateRange, setNonMotorSelectedDateRange] = useState<SelectedDateRange>();
    const { user } = useUserAccount()
    const handleTabChange = (tab: string) => {
        setActiveTab(tab as IPolicyTypes);
    };

    useEffect(() => {
        const resizeCallback = () => {
            setScreenWidth(document.documentElement.clientWidth);
        };
        window.addEventListener('resize', resizeCallback);

        return () => {
            window.removeEventListener('resize', resizeCallback);
        };
    }, []);

    const activeButtonClassNames = ` text-black bg-[#FBC02D] shadow px-3 py-1 text-sm font-medium rounded-full w-full whitespace-nowrap`;
    const inActiveButtonClassNames = 'text-[#5B5B5B] px-3 py-1 text-sm font-medium rounded-full w-full whitespace-nowrap';

    const getNonMotorDistrubitionData = async () => {
        const from = nonMotorSelectedDateRange?.from
        const to = nonMotorSelectedDateRange?.to

        try {
            const res: any = await getBrokerDashboardNonMotorPolicyData(from, to);
            if (res) {
                const data: PolicyStats = res?.data;
                return {
                    totalExpiredPolicies: data?.totalExpiredPolicies,
                    totalActivePolicies: data?.totalActivePolicies,
                    countByType: data?.countByType,
                    premiumByType: data?.premiumByType
                };
            }
        } catch (error) {
            console.error(error);
        }
    };

    const getMotorPolicyTypeDistributionData = async () => {
        const from = motorSelectedDateRange?.from
        const to = motorSelectedDateRange?.to
        const companyId = user?.company?.id;

        try {
            if (companyId) {
                const res: any = await getBrokerDashboardProtectionTypes(Number(companyId), from, to);
                if (res) {
                    const data = res?.data;
                    return {
                        protectionTypes: data?.protectionTypes || [],
                        counts: data?.counts || [],
                        amounts: data?.amounts || []
                    };
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    const { data: motorPolicyDistributionData, isLoading: isLoadingMotorPolicyDistribution } = useQuery({
        queryKey: ['getMotorPolicyTypeDistributionData', motorSelectedDateRange],
        queryFn: getMotorPolicyTypeDistributionData,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });

    const { data: nonMotorPolicyDistributionData, isLoading: isLoadingNonMotorPolicyDistribution } = useQuery({
        queryKey: ['getNonMotorDistrubitionData', nonMotorSelectedDateRange],
        queryFn: getNonMotorDistrubitionData,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
    });


    return (
        <div className='bg-white dark:bg-darkBg rounded-2xl shadow-sm border border-gray-200 dark:border-darkBg-border p-5'>
            <div
                className={clsx(
                    'flex items-center ',
                    { ['flex flex-row justify-between w-full ']: screenWidth > 1213 },
                    { ['flex-row space-x-2']: screenWidth <= 1213 }
                )}>
                <div className={` md:w-full pb-4 md:pb-2 text-md sm:text-md md:text-xl font-bold dark:text-darkBg-mainText `}>
                    Policy Distribution
                </div>
                <div className={` flex items-center space-x-2 bg-gray-100 p-1 rounded-full`}>
                    <button
                        className={` ${activeTab === 'motor' ? activeButtonClassNames : inActiveButtonClassNames
                            }`}
                        onClick={() => handleTabChange('motor')}>
                        Motor
                    </button>
                    <button
                        className={`rounded-full ${activeTab === 'non-motor' ? activeButtonClassNames : inActiveButtonClassNames
                            }`}
                        onClick={() => handleTabChange('non-motor')}>
                        Non-Motor
                    </button>
                </div>
            </div>

            <div className="h-full flex flex-col">
                <div className="py-2">
                    <DashboardDatePickers
                        onSubmit={(date: SelectedDateRange) => {
                            if (activeTab === 'motor') {
                                setMotorSelectedDateRange(date);
                            } else {
                                setNonMotorSelectedDateRange(date);
                            }
                        }}
                        initialStartDate={activeTab === 'motor' ? motorSelectedDateRange?.from : nonMotorSelectedDateRange?.from}
                        initialEndDate={activeTab === 'motor' ? motorSelectedDateRange?.to : nonMotorSelectedDateRange?.to}
                        autoSubmit={true}
                        debounceMs={300}
                    />
                </div>

                <div className="">
                    <div className={activeTab === 'motor' ? "" : "hidden"}>
                        <MotorPolicyDistributionChart
                            motorPolicyDistributionData={motorPolicyDistributionData}
                            isLoading={isLoadingMotorPolicyDistribution} 
                            isRangeSelected={motorSelectedDateRange ? true : false}/>
                    </div>

                    <div className={activeTab === 'non-motor' ? "" : "hidden"}>
                        <NonMotorPolicyDistributionChart
                            nonMotorPolicyDistributionData={nonMotorPolicyDistributionData}
                            isLoadingPolicyDistribution={isLoadingNonMotorPolicyDistribution} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PolicyDistribution;


interface TabProps {
    name: string;
    children: React.ReactNode;
}

type IPolicyTypes = 'motor' | 'non-motor';